import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { nearestTabIndexToCentre, prefersReducedMotion } from '../../utils/swipeTabs';
import { edgeFadeMask, readEdgeFade } from './edgeFade';

/**
 * THE shared tab strip — a horizontal `role="tablist"` that sits between a
 * `ui/PageHeaderBand` and a `ui/Toolbar`. Added 2026-09-12 for Department
 * Orders' queue tabs.
 *
 * Product owner, asking for them: tabs *"like on admin settings"*, and
 * *"Lets test this on staging so we can see if its any good before we think
 * about adding it to other pages. BUT build it as a common tab interface that
 * can be reused."* So the reuse is the requirement, not an aspiration — which
 * is why `persistKey` and `hashSync` exist from the first commit even though
 * the only caller today uses just the first.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * 🪤 IT GOES UNDER THE HEADER BAND, NEVER ON THE TOOLBAR
 * ═══════════════════════════════════════════════════════════════════════════
 * `ui/CLAUDE.md`: *"THE RULE THAT DECIDES EVERY CASE NOT LISTED HERE: the
 * sticky band holds only what you reach for WHILE SCROLLING."* A queue tab is
 * a scope you set once and then work inside; you do not reach for it mid-scroll
 * the way you reach for search. And `client/src/pages/CLAUDE.md` has a standing
 * ruling that the toolbar carries **five control groups at most**, because the
 * owner has twice complained that the bar wraps — a sixth group made of six
 * tabs is the worst possible thing to add to it. Render this ABOVE the toolbar
 * and below the header band.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * ♿ SEMANTICS — the same contract `ui/FiltersMasterDetail`'s rail already uses
 * ═══════════════════════════════════════════════════════════════════════════
 * `role="tablist"` + `role="tab"` + `aria-selected`, roving `tabIndex` (exactly
 * one tab is in the tab order, so Tab moves THROUGH the strip rather than
 * around every tab), arrow keys with wraparound on BOTH axes, `Home`/`End`, and
 * activation following focus. That file's reasoning is reused wholesale rather
 * than re-argued; two points are worth restating here because they are about
 * THIS geometry:
 *   - **Both axes of arrow key, even though this strip is horizontal.**
 *     `aria-orientation` cannot carry a breakpoint and the strip WRAPS to two
 *     rows on a narrow screen, so a horizontal-only contract would be a lie at
 *     exactly the width where it matters.
 *   - **`aria-controls` is omitted when no `panelId` is given.** A dangling
 *     `aria-controls` is a broken relationship — assistive technology announces
 *     a panel that does not exist — which is worse than no association at all.
 *     A caller whose "panel" is a whole paginated list (Department Orders) has
 *     no single element to name and correctly passes nothing.
 *   - The tabs are real `<button type="button">`, so `index.css`'s global focus
 *     ring applies and no `focus:outline-none` appears in this file. The
 *     explicit `type` is not decoration: a bare `<button>` inside a form
 *     defaults to `type="submit"`, the measured trap recorded in
 *     `client/src/pages/CLAUDE.md` against the actions-in-header move.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * 🎨 THE ACTIVE INDICATOR IS NOT COLOUR
 * ═══════════════════════════════════════════════════════════════════════════
 * The root `CLAUDE.md` sole-carrier rule (written up at `DueIndicator.js`, and
 * the reason `bg-amber-500` at 2.15:1 shipped and had to be corrected): a
 * signal that is the ONLY thing saying "this one" has to reach a user who
 * cannot separate the hues. Here it never is the only thing — the active tab
 * carries an **underline** (a geometric change), a **heavier font weight**
 * (text), `aria-selected` (semantics) AND a colour. Any one of the first three
 * survives on its own.
 *
 * 🪤 **`border-b-2 border-transparent` on the inactive tabs, never `border-0`.**
 * The border box must be RESERVED or the whole strip shifts 2px the instant a
 * tab is selected — the same rule `TOOLBAR_GHOST` records for its hover border.
 *
 * 📱 Below `sm` the strip is a horizontal scroll region (`overflow-x-auto`) with
 * `whitespace-nowrap` tabs, so six labels do not shred into six rows on a
 * phone. ⚠️ **jsdom applies no stylesheet and lays nothing out, so neither the
 * underline, the scroll, nor any contrast figure is evidenced by
 * `Tabs.test.js`** — that file says so, and those remain BROWSER checks.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * 🔑 IT IS CONTROLLED, AND IT KNOWS NOTHING ABOUT ANY SURFACE
 * ═══════════════════════════════════════════════════════════════════════════
 * It takes `tabs` (already built, already counted, already gated), `active` and
 * `onChange`. It never reads an order, a filter model or a permission — the
 * same contract `ui/PageHeaderBand`, `OrderActionsMenu` and `OrderTableView`
 * have, and for the same reason: a shared component with a `count` derivation
 * or an `isAdmin` prop is four pages' logic wearing one component's name.
 *
 * 🪤 **A `count` of `0` RENDERS.** `0` is a real answer — "nothing waiting in
 * this queue" is the reassuring number a buyer wants — so the test is
 * `count != null`, never `count &&`. This repo's recurring defect class.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * 🚫 A DISABLED TAB (2026-09-22) — `aria-disabled`, NOT the native attribute
 * ═══════════════════════════════════════════════════════════════════════════
 * Added for the Gag Breakdown's `Table | Timeline | Labour` switcher, whose
 * Timeline and Labour tabs go dead while "Show deleted gags" is on (deleted
 * gags have no timeline). Per-tab and OPT-IN: `tabs[n].disabled` defaults
 * falsy, so every existing caller renders byte-identically.
 *
 * 🔑 **THE NAMING IS THE HOUSE PAIR, NOT AN INVENTION.**
 * `components/OrderItemReceivedControls.js` already ships `disabled` +
 * `disabledReason` — a `title` override that applies **only while `disabled`**,
 * so a future busy-only disable keeps the control's ordinary title. Same
 * contract here, same default (`null`), for the same reason.
 *
 * 🚨 **`aria-disabled`, DELIBERATELY NOT `disabled`, AND THE TWO ARE NOT
 * INTERCHANGEABLE HERE.** A natively disabled `<button>` leaves the focus order
 * and fires no pointer events — which in a ROVING-TABINDEX strip is the worse
 * failure: the tab vanishes from the keyboard's model of the strip while still
 * occupying a visible slot, and the browser suppresses its `title`, so the very
 * tooltip that explains *why* it is dead becomes unreachable by the pointer too.
 * `aria-disabled` keeps the tab announced, keeps the tooltip, and this file
 * refuses the activation itself — `select()` returns early, so a click, an
 * arrow, `Home` and `End` all decline through ONE gate rather than four.
 *
 * ♿ **ARROW KEYS SKIP IT.** `nextEnabled` steps over disabled tabs in either
 * direction with the same wraparound, `Home`/`End` land on the first/last
 * ENABLED tab, and the roving stop never parks on a dead one. A strip that
 * moves focus onto a tab which then refuses to activate is worse than no
 * keyboard support at all — the key press looks broken rather than declined.
 * 🪤 Every walk is bounded by `tabs.length`: an all-disabled strip returns -1
 * and nothing moves, instead of spinning looking for a tab that is not there.
 *
 * 🎨 The disabled shades are carried over VERBATIM from the switcher this
 * replaced (`text-gray-300 dark:text-gray-600 cursor-not-allowed`), so the
 * control looks the same before and after the migration. ⚠️ **jsdom evidences
 * none of that** — see the stylesheet warning above.
 *
 * Props:
 *  - tabs: `[{ id, label, count?, disabled?, disabledReason? }]` — order is the
 *    rendered order. `disabled` greys the tab and refuses every route to
 *    selecting it; `disabledReason` is its `title`, ignored while enabled.
 *  - active: the selected id. An id matching no tab selects NOTHING (rather
 *    than guessing at the first), but the first tab still holds the roving stop
 *    so the strip stays keyboard-reachable.
 *  - onChange(id): fired on click, arrow/Home/End, and on an adoption from
 *    `persistKey` / `hashSync` at mount. Never fired for the already-active id.
 *  - persistKey: optional `sfx_`-prefixed localStorage key. See below.
 *  - hashSync: optional; mirrors `AdminSettings.js`'s `replaceState` behaviour.
 *  - panelId: optional id of the panel these tabs control.
 *  - idPrefix: `${idPrefix}-tab-<id>` ids AND testids; `${idPrefix}-tab-count-<id>`.
 *  - ariaLabel: the tablist's accessible name. Required in practice — a
 *    tablist with no name is announced as an unlabelled group of tabs.
 *  - scrollHints: optional; below `sm`, scroll the active tab into view and
 *    fade the edge(s) with more tabs behind them. See the prop's own note.
 *  - className: appended to the strip wrapper.
 */

/**
 * Read a persisted tab id, validated against the ids that exist TODAY.
 *
 * 🔑 **EXPORTED SO A PAGE CAN SEED ITS OWN STATE SYNCHRONOUSLY.** The component
 * will adopt a stored tab on its own (see the mount effect), but a page that
 * filters a list by the tab wants the right answer on the FIRST render — an
 * adoption through an effect paints the wrong tab's rows for one frame and
 * costs a wasted filter pass over the whole list. `DepartmentOrders.js` calls
 * this in a lazy `useState` initialiser, which makes the component's mount
 * adoption a no-op there.
 *
 * 🪤 **The validation is not optional: storage OUTLIVES A RELEASE.** A tab id
 * that is renamed or retired would otherwise strand a user on a tab that no
 * longer exists — the list would be filtered by a predicate nothing matches and
 * the page would read as empty. Unknown → `fallback`.
 *
 * 🪤 **Every access is wrapped**: `localStorage` throws outright in a private
 * window and where site data is blocked, and a tab strip must not take a page
 * down over a preference.
 */
export function readStoredTabId(persistKey, validIds, fallback) {
  if (!persistKey) return fallback;
  try {
    const stored = window.localStorage.getItem(persistKey);
    if (stored && validIds.includes(stored)) return stored;
  } catch (e) { /* private window / blocked site data — fall through */ }
  return fallback;
}

/** The same validated read, for a `#hash`. Exported for symmetry and testing. */
export function readHashTabId(validIds, fallback) {
  try {
    const hash = window.location.hash.replace(/^#/, '');
    if (hash && validIds.includes(hash)) return hash;
  } catch (e) { /* no window.location in some harnesses */ }
  return fallback;
}

/* The separator for the composite key that memoises the tab-id LIST. A raw
   NUL BYTE was written here by accident and lived in the file until
   2026-09-12: it works (a NUL is a legal string character) and every test was
   green, but GNU grep then reports "Binary file Tabs.js matches" and prints NO
   LINE CONTENT, and git's checkout filter skips CRLF normalisation for it. In a
   repo whose method is "cite the call shape and grep for it", a source file
   that greps blind is a real handicap. A visible sentinel costs nothing and
   cannot be typed by a tab id. */
const SEP = '\u0000';

// 📱 `max-md:grow max-md:justify-center` (owner 2026-10-03: "if the tabs could
// fit they should be spaced to fit"): when the strip has room, the tabs share
// it evenly; when it overflows there is no free space, so nothing changes and
// the strip scrolls as before.
const TAB_BASE =
  'shrink-0 whitespace-nowrap px-3 py-2 border-b-2 text-sm transition-colors ' +
  'max-md:grow max-md:justify-center ' +
  'min-h-[44px] sm:min-h-0 sm:h-[2.375rem] inline-flex items-center gap-1.5 rounded-t-md';

/* 📐 `equal` (opt-in, 2026-10-27, the timesheet's phone day strip — owner: "not obvious that you can
   swipe sideways", chose the calendar WEEK STRIP): every tab gets an EQUAL share of the row and the
   label may wrap to two lines, so all seven days fit and nothing is hidden behind a scroll. A SEPARATE
   base, not classes stacked on TAB_BASE — two conflicting utilities resolve by stylesheet order. */
const TAB_BASE_EQUAL =
  'flex-1 basis-0 min-w-0 px-0 py-1 border-b-2 text-sm transition-colors ' +
  'min-h-[44px] inline-flex flex-col items-center justify-center rounded-t-md text-center leading-tight';

/* 🪤 **`-mb-px` IS BAND CHROME, NOT TAB CHROME, AND IT ONLY MEANS ANYTHING
   WITH THE BAND.** It hangs each tab 1px below the row so its own `border-b-2`
   sits ON the wrapper's `border-b` instead of above it. Under `bare` there is no
   wrapper border to sit on, so the same class is a 1px downward shift of every
   tab inside a toolbar whose other controls are vertically centred — and below
   `sm` it pushes against the `overflow-y-hidden` clip added on 2026-09-12. Split
   out of `TAB_BASE` rather than left in it, so the two cannot drift apart. */
const TAB_OVERLAP = '-mb-px';

/* 🎨 THREE channels, and the colour is the least of them — see the docblock.
   Both shades carry an explicit `dark:` pair rather than leaning on
   `index.css`'s global `!important` override table, because a tab strip is new
   surface and an inert `dark:` class reads as explicit dark support to the next
   reader. */
const TAB_ON =
  'border-blue-600 text-blue-700 font-semibold ' +
  'dark:border-blue-400 dark:text-blue-300';

/* 📱 `indicatorAt` (opt-in, owner 2026-10-31: "can you blue line under the day scroll smoothly and follow
   the swiping action?"): the active tab keeps its colour + weight, and the UNDERLINE becomes ONE bar that
   slides to a FRACTIONAL tab position the caller supplies (the phone day strip's scroll). A separate
   constant, never `border-transparent` stacked on TAB_ON (two border colours resolve by stylesheet order). */
const TAB_ON_SLIDING =
  'border-transparent text-blue-700 font-semibold dark:text-blue-300';
const SLIDING_BAR = 'pointer-events-none absolute bottom-0 left-0 h-0.5 rounded-full bg-blue-600 dark:bg-blue-400';

const TAB_OFF =
  'border-transparent text-gray-600 font-medium ' +
  'hover:text-gray-800 hover:border-gray-300 ' +
  'dark:text-gray-400 dark:hover:text-gray-200 dark:hover:border-gray-600';

const COUNT_ON =
  'ml-0.5 rounded-full px-1.5 py-0.5 text-xs font-semibold tabular-nums ' +
  'bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-200';

const COUNT_OFF =
  'ml-0.5 rounded-full px-1.5 py-0.5 text-xs font-medium tabular-nums ' +
  'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300';

/* `compact` (below `md` only): tighter tab padding/gap and a slimmer badge. */
const TAB_COMPACT = 'max-md:px-2 max-md:gap-1';
const COUNT_COMPACT = 'max-md:ml-0 max-md:px-1 max-md:text-[0.6875rem]';
const LIST_COMPACT = 'max-md:gap-0';

/* 🚫 The dead tab. Carried over VERBATIM from the Breakdown's segmented
   switcher this replaced, so the migration changes no shade. It keeps
   `border-transparent` and `font-medium` from `TAB_OFF` for the same reasons
   those are there — the reserved 2px border box, and a weight that does not
   move the label when the tab comes back to life. No hover pair: a control
   that declines to act must not light up under the pointer. */
const TAB_DISABLED =
  'border-transparent font-medium cursor-not-allowed ' +
  'text-gray-300 dark:text-gray-600';

/* `snapCenter`: below `md` (the app's mobile breakpoint, `(max-width: 767px)`
   everywhere else). Quiet time before a user scroll counts as settled where the
   browser has no `scrollend`, and the delay before re-centring after one. */
export const SNAP_CENTER_QUERY = '(max-width: 767px)';
export const SNAP_SETTLE_MS = 140;
export const SNAP_RECENTRE_MS = 350;

function matchesQuery(q) {
  try {
    return typeof window !== 'undefined' && typeof window.matchMedia === 'function'
      && window.matchMedia(q).matches;
  } catch (e) { return false; }
}

/* The edge-fade mask for `scrollHints` — the shared `ui/edgeFade` rule. */
const fadeMask = edgeFadeMask;

export default function Tabs({
  // Optional: a tap on the ALREADY-ACTIVE tab (owner 2026-10-03: the order pages
  // collapse every opened row on any tab tap). Absent → that tap does nothing, as before.
  onReselect,
  tabs = [],
  active,
  onChange,
  persistKey,
  hashSync = false,
  panelId,
  idPrefix = 'tabs',
  ariaLabel,
  /* 🔄 RENDER WITHOUT THE BAND CHROME (2026-09-12) — for a caller putting the
     strip inside something that ALREADY has chrome. Owner: *"make sure the search
     box is on the tab line"*; on Department Orders the strip is now the LEFT GROUP
     of `ui/Toolbar` rather than a band of its own, which is one sticky row rather
     than two and is the vertical height he asked to save.
     🔑 **WHY A BOOLEAN AND NOT `className`, which is the obvious answer and the
     wrong one.** The wrapper carries `border-b … mb-3`; cancelling those through
     `className` means passing `border-0 mb-0` and hoping the stylesheet favours
     you — and `ui/Toolbar.js`'s own `marginClassName` docblock records exactly that
     trap: *"two conflicting Tailwind utilities on one element (`mb-3 mb-2`) resolve
     by stylesheet order, not by the order you wrote them."* `bare` DROPS the chrome
     rather than fighting it. `className` still exists and still composes on top;
     the two are not alternatives.
     🚨 **IT DROPS CHROME ONLY.** `role="tablist"`/`role="tab"`, `aria-selected`,
     the roving `tabIndex`, the keyboard, the counts and the active UNDERLINE all
     survive — the underline lives on the TAB, not on the band, and it is the
     non-colour carrier the sole-carrier rule turns on. Pinned item by item by
     §10 of `Tabs.test.js`. */
  bare = false,
  /* 📱 MOBILE SCROLL AFFORDANCES (2026-09-30, My Orders). Opt-in, default off,
     so every other caller renders byte-identically. Owner: *"one horizontally
     scrolling tab row, active tab scrolled into view, edge fade."* Below `sm`
     the strip is already a horizontal scroll box; this adds the two things that
     make a scroll box READ as one:
       - the ACTIVE tab is scrolled into view whenever it changes (a tab chosen
         from a deep link, or the seventh tab, is otherwise off-screen), by
         moving the strip's own `scrollLeft` — never `scrollIntoView`, which
         would also scroll the PAGE;
       - a CSS mask FADES the edge that has more tabs behind it, and drops the
         fade on an edge that is fully scrolled. A mask, not an overlay, so it
         is colour-agnostic and needs no `dark:` pair.
     🪤 Only while the strip actually OVERFLOWS and only below `sm` (640px):
     above it the tablist is `overflow-visible`, and a mask there would clip
     real tabs. ⚠️ jsdom lays nothing out, so the fade and the scroll are
     BROWSER checks; `Tabs.test.js` pins only that the scroll is asked for. */
  scrollHints = false,
  /* 📐 EQUAL-WIDTH TABS (opt-in, default off — every other caller byte-identical): see TAB_BASE_EQUAL.
     `label` may then be a node (rendered as-is, e.g. two stacked lines). */
  equal = false,
  // 📱 A fractional tab index (e.g. 3.4) → the underline is ONE sliding bar at that position (see
  // TAB_ON_SLIDING). null/absent = the ordinary per-tab underline, byte-identical.
  indicatorAt = null,
  /* 📱 SNAP-TO-CENTRE (2026-09-30, My Orders, below `md`). Opt-in, default
     off, so every other caller renders byte-identically. Owner: the strip
     snaps its tabs to the centre, **the tab that SETTLES in the centre becomes
     active** (no tap needed), and the neighbours peek left and right.
       - Below `md` the strip is a `snap-x snap-mandatory` scroll box whose
         inline padding is MEASURED so the first and last tab can reach the
         centre too; `md` and up it is the ordinary strip.
       - The active tab is CENTRED whenever it changes — which is how the strip
         "follows" a swipe on the list (`hooks/useSwipeTabs.js`).
       - Activation-on-settle fires ONLY for a scroll the USER started (touch
         or wheel on the strip) and only once the finger is up and the scroll
         has stopped (`scrollend`, or `SNAP_SETTLE_MS` of quiet where that
         event does not exist). 🪤 **Our own centring never activates
         anything** — a tap disarms it, and so does every programmatic centre,
         or a smooth scroll passing THROUGH a tab would select it mid-flight.
       - It goes through `select`, the ONE gate: a disabled tab is refused,
         and the caller's `onChange` (a dirty-edit prompt on My Orders) is
         honoured. If the tab does not become active, the strip re-centres on
         the one that is.
     ♿ The keyboard and screen-reader contract is untouched: same roles, same
     roving `tabIndex`, same arrow keys. ⚠️ jsdom lays nothing out — the snap,
     the peek and the feel are BROWSER checks; `Tabs.snapCenter.test.js` pins
     the decisions over mocked geometry. */
  snapCenter = false,
  /* 📱 FLUSH START (2026-10-01, My Orders, below `md`). Opt-in, only read
     under an active `snapCenter`. Owner: *"The tab strip starts flush left,
     right after the search icon; swipe/snap behaviour unchanged."* The
     measured LEFT padding is dropped (the right one stays), so at rest the
     first tab sits at the strip's left edge instead of in its centre.
     🔑 The consequence, and the one rule it adds: a tab whose centre lies
     left of the strip's centre at scroll 0 CANNOT be centred, so at the
     start edge "nearest the centre" would wrongly pick a later tab. A rest
     at the start edge therefore keeps the active tab when it is one of
     those, and otherwise selects the FIRST tab. Every other rest is the
     unchanged nearest-to-centre rule. Omitted, byte-identical. */
  snapFlushStart = false,
  /* 📱 COMPACT (opt-in, 2026-10-02, My Tasks): below `md` the tabs give back
     horizontal padding, the inter-tab gap and some count-badge width so a
     short strip FITS a 360px phone with no horizontal scroll. The 44px tap
     height, the underline, the weight and `aria-selected` are untouched —
     only `max-md:` spacing classes are added. Omitted, byte-identical. */
  compact = false,
  className = '',
}) {
  const tabRefs = useRef({});
  const listRef = useRef(null);
  // 📱 The sliding underline (`indicatorAt`): placed by MEASURING the two tabs it sits between, so it
  // matches each tab's real width; written straight to the bar's style (no re-render per frame).
  const sliding = indicatorAt != null && Number.isFinite(Number(indicatorAt));
  const barRef = useRef(null);
  useLayoutEffect(() => {
    const bar = barRef.current;
    if (!sliding || !bar || tabs.length === 0) return;
    const p = Math.max(0, Math.min(tabs.length - 1, Number(indicatorAt)));
    const lo = Math.floor(p);
    const hi = Math.min(tabs.length - 1, lo + 1);
    const a = tabRefs.current[tabs[lo].id];
    const b = tabRefs.current[tabs[hi].id];
    if (!a || !b) return;
    const f = p - lo;
    bar.style.width = `${a.offsetWidth + (b.offsetWidth - a.offsetWidth) * f}px`;
    bar.style.transform = `translateX(${a.offsetLeft + (b.offsetLeft - a.offsetLeft) * f}px)`;
  });
  const [fade, setFade] = useState({ left: false, right: false });
  /* What storage held when we mounted. The write effect compares against this
     so an ADOPTED value is never written back — see the hydration rule below. */
  const storedRef = useRef(null);
  const hydrated = useRef(false);
  /* 🚨 THE WRITE EFFECT MUST SKIP ITS FIRST RUN, AND THIS REF IS THE ONLY
     THING THAT MAKES IT. React commits effects in DECLARATION order within one
     pass, so on mount the adopt effect runs (setting `hydrated`) and the write
     effect runs immediately after it, in the SAME commit — a `hydrated` guard
     alone is therefore already satisfied and stamps the default into storage
     for every visitor. Measured: `Tabs.test.js` §5's hydration case read
     `"all"` out of the key with exactly that implementation. */
  const skipFirstWrite = useRef(true);

  const ids = tabs.map((t) => t.id);
  const idsKey = ids.join(SEP);
  const activeIndex = ids.indexOf(active);

  /* 🪤 THE LATEST `tabs`, THROUGH A REF, SO `select`'S DEPS DO NOT MOVE.
     Callers pass an inline array, so listing `tabs` as a dependency would mint
     a new `select` every render — and the `hashchange` effect below hangs off
     `select`, so it would tear down and re-add a window listener on every
     render of the page. The deps stay exactly what they were before disabled
     tabs existed. */
  const tabsRef = useRef(tabs);
  tabsRef.current = tabs;

  /* 🚨 THE ONE GATE. Click, arrow, Home, End and a `hashchange` all arrive
     here, so a disabled tab is refused ONCE rather than at four call sites —
     and a pasted `#hash` naming a dead tab is declined by the same line. */
  const select = useCallback((id) => {
    if (id == null) return;
    if (id === active) { if (onReselect) onReselect(id); return; }
    const tab = tabsRef.current.find((t) => t.id === id);
    if (!tab || tab.disabled) return;
    if (onChange) onChange(id);
  }, [active, onChange, onReselect]);

  /* ── MOUNT: adopt a persisted / linked tab ──────────────────────────────
     🔑 **HASH BEATS STORAGE**, matching `AdminSettings.js`'s own order. A
     pasted `#to-order` link must win over the reader's last position, or the
     link silently does nothing — which is the whole point of having one.
     Runs once: a later change to `tabs` must not re-adopt and yank the user
     back to where they were when the page loaded. */
  useEffect(() => {
    let adopted = null;
    if (hashSync) adopted = readHashTabId(ids, null);
    if (adopted == null) adopted = readStoredTabId(persistKey, ids, null);
    storedRef.current = adopted;
    hydrated.current = true;
    /* 🚫 THROUGH `select`, NOT A BARE `onChange` — otherwise storage and a
       pasted `#hash` are the two routes to a DISABLED tab that every other
       route refuses. `select` already carries this line's own
       `adopted !== active` guard, so the behaviour is unchanged for every
       caller that declares no disabled tab. */
    if (adopted != null) select(adopted);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* ── WRITE on a real change ─────────────────────────────────────────────
     🪤 **IT DOES NOT WRITE ON HYDRATION, AND THAT IS THE `useColumnWidths`
     RULE RESTATED.** Stamping the default into the key merely because somebody
     visited the page means a later change to that default is silently
     overridden for every one of them by a value nobody chose. So: nothing is
     written before the mount effect has run, and nothing is written for a value
     that is simply what we just read. */
  useEffect(() => {
    if (!persistKey || !hydrated.current) return;
    if (skipFirstWrite.current) { skipFirstWrite.current = false; return; }
    if (active === storedRef.current) return;
    try {
      window.localStorage.setItem(persistKey, active);
      storedRef.current = active;
    } catch (e) { /* private window / blocked site data */ }
  }, [active, persistKey]);

  /* ── MIRROR the active tab into the hash ────────────────────────────────
     `replaceState`, never `pushState` — `AdminSettings.js` does the same, and
     for the reason its own code implies: a tab is a VIEW of one page, so
     stacking a history entry per tab press turns Back into "undo my last six
     glances" instead of "leave this page". */
  useEffect(() => {
    if (!hashSync || !hydrated.current) return;
    if (!ids.includes(active)) return;
    try {
      if (window.location.hash.replace(/^#/, '') !== active) {
        // `window.history.state`, never `null` (R14, 2026-09-15): the data
        // router keeps its entry index there, and Back from a guarded page
        // onto an entry whose state was nulled cannot be blocked.
        window.history.replaceState(window.history.state, '', `#${active}`);
      }
    } catch (e) { /* history unavailable */ }
    // `idsKey` rather than `ids`: a fresh array every render would re-run this.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, hashSync, idsKey]);

  /* ── LISTEN for a hash the browser changed (Back, a pasted link) ────────── */
  useEffect(() => {
    if (!hashSync) return undefined;
    const onHash = () => {
      const next = readHashTabId(idsKey.split(SEP).filter(Boolean), null);
      if (next != null) select(next);
    };
    window.addEventListener('hashchange', onHash);
    return () => window.removeEventListener('hashchange', onHash);
  }, [hashSync, idsKey, select]);

  /* ── 📱 scrollHints: keep the active tab in view, and fade the edges ──── */
  const isNarrow = () => {
    try {
      return typeof window !== 'undefined' && typeof window.matchMedia === 'function'
        && window.matchMedia(snapCenter ? SNAP_CENTER_QUERY : '(max-width: 639px)').matches;
    } catch (e) { return false; }
  };
  const updateFade = useCallback(() => {
    const list = listRef.current;
    if (!scrollHints || !list) return;
    const next = isNarrow() ? readEdgeFade(list) : { left: false, right: false };
    setFade((prev) => (prev.left === next.left && prev.right === next.right ? prev : next));
  }, [scrollHints]);

  /* ── 📱 snapCenter ─────────────────────────────────────────────────────── */
  const [snapActive, setSnapActive] = useState(() => snapCenter && matchesQuery(SNAP_CENTER_QUERY));
  useEffect(() => {
    if (!snapCenter || typeof window === 'undefined' || typeof window.matchMedia !== 'function') return undefined;
    const mq = window.matchMedia(SNAP_CENTER_QUERY);
    const handler = (e) => setSnapActive(Boolean(e.matches));
    setSnapActive(Boolean(mq.matches));
    if (typeof mq.addEventListener === 'function') mq.addEventListener('change', handler);
    return () => { if (typeof mq.removeEventListener === 'function') mq.removeEventListener('change', handler); };
  }, [snapCenter]);

  const activeRef = useRef(active);
  activeRef.current = active;
  /* `armed` = the user started a scroll of the strip; `touching` = a finger is
     still down. Both refs: they change on every touch and must not render. */
  const userScroll = useRef({ armed: false, touching: false });
  const settleTimer = useRef(null);
  const recentreTimer = useRef(null);
  useEffect(() => () => {
    clearTimeout(settleTimer.current);
    clearTimeout(recentreTimer.current);
  }, []);

  const centreOn = useCallback((id, smooth) => {
    const list = listRef.current;
    const el = tabRefs.current[id];
    if (!list || !el || typeof el.getBoundingClientRect !== 'function') return;
    const lr = list.getBoundingClientRect();
    const er = el.getBoundingClientRect();
    const delta = (er.left + er.width / 2) - (lr.left + lr.width / 2);
    const left = Math.max(0, list.scrollLeft + delta);
    // Programmatic: it must never read as a user scroll to `settle`.
    userScroll.current.armed = false;
    if (typeof list.scrollTo === 'function') {
      list.scrollTo({ left, behavior: smooth ? 'smooth' : 'auto' });
    } else {
      list.scrollLeft = left;
    }
  }, []);

  const settle = useCallback(() => {
    clearTimeout(settleTimer.current);
    settleTimer.current = null;
    const u = userScroll.current;
    if (!u.armed || u.touching) return;
    u.armed = false;
    const list = listRef.current;
    if (!list) return;
    const lr = list.getBoundingClientRect();
    const current = tabsRef.current;
    const rects = current.map((t) => {
      const el = tabRefs.current[t.id];
      return el && typeof el.getBoundingClientRect === 'function' ? el.getBoundingClientRect() : null;
    });
    const centreX = lr.left + lr.width / 2;
    let i = nearestTabIndexToCentre(centreX, rects);
    if (snapFlushStart && list.scrollLeft <= 1) {
      // At the start edge (see `snapFlushStart`): the active tab stays if it
      // cannot be centred anyway; otherwise the strip is back at its first tab.
      const ai = current.findIndex((t) => t.id === activeRef.current);
      const ar = ai >= 0 ? rects[ai] : null;
      i = ar && ar.left + ar.width / 2 <= centreX ? ai : 0;
    }
    if (i >= 0 && current[i].id !== activeRef.current) select(current[i].id);
    // A disabled tab, a held (dirty-edit) change or an off-centre rest all end
    // the same way: the strip goes back to showing the ACTIVE tab centred.
    clearTimeout(recentreTimer.current);
    recentreTimer.current = setTimeout(
      () => centreOn(activeRef.current, !prefersReducedMotion()),
      SNAP_RECENTRE_MS,
    );
  }, [select, centreOn, snapFlushStart]);

  const armSettle = useCallback(() => {
    clearTimeout(settleTimer.current);
    settleTimer.current = setTimeout(settle, SNAP_SETTLE_MS);
  }, [settle]);

  /* Measured side padding, so the FIRST and LAST tab can sit in the centre. */
  const updateSnapPadding = useCallback(() => {
    const list = listRef.current;
    if (!list) return;
    if (!snapActive) {
      list.style.paddingLeft = '';
      list.style.paddingRight = '';
      return;
    }
    const current = tabsRef.current;
    const widthOf = (t) => {
      const el = t && tabRefs.current[t.id];
      return el && typeof el.getBoundingClientRect === 'function' ? el.getBoundingClientRect().width : 0;
    };
    const w = list.clientWidth;
    list.style.paddingLeft = snapFlushStart ? '0px' : `${Math.max(0, (w - widthOf(current[0])) / 2)}px`;
    list.style.paddingRight = `${Math.max(0, (w - widthOf(current[current.length - 1])) / 2)}px`;
  }, [snapActive, snapFlushStart]);

  useEffect(() => {
    if (!snapCenter) return undefined;
    updateSnapPadding();
    if (snapActive) centreOn(activeRef.current, false);
    if (typeof window === 'undefined') return undefined;
    window.addEventListener('resize', updateSnapPadding);
    return () => window.removeEventListener('resize', updateSnapPadding);
  }, [snapCenter, snapActive, idsKey, updateSnapPadding, centreOn]);

  useEffect(() => {
    const list = listRef.current;
    if (!snapActive || !list) return undefined;
    const onEnd = () => { if (userScroll.current.armed && !userScroll.current.touching) settle(); };
    list.addEventListener('scrollend', onEnd);
    return () => list.removeEventListener('scrollend', onEnd);
  }, [snapActive, settle]);

  const snapHandlers = snapActive ? {
    onTouchStart: () => {
      clearTimeout(recentreTimer.current);
      userScroll.current = { armed: true, touching: true };
    },
    onTouchEnd: () => { userScroll.current.touching = false; armSettle(); },
    onTouchCancel: () => { userScroll.current.touching = false; armSettle(); },
    onWheel: () => { clearTimeout(recentreTimer.current); userScroll.current.armed = true; armSettle(); },
  } : {};

  useEffect(() => {
    if (snapActive) {
      centreOn(active, !prefersReducedMotion());
      updateFade();
      return;
    }
    if (!scrollHints) return;
    const list = listRef.current;
    const el = tabRefs.current[active];
    if (list && el && typeof el.getBoundingClientRect === 'function') {
      const lr = list.getBoundingClientRect();
      const er = el.getBoundingClientRect();
      const PAD = 24; // clear the fade, so the active tab is never the faded one
      if (er.left < lr.left + PAD) list.scrollLeft -= (lr.left + PAD - er.left);
      else if (er.right > lr.right - PAD) list.scrollLeft += (er.right - (lr.right - PAD));
    }
    updateFade();
  }, [active, scrollHints, updateFade, snapActive, centreOn]);

  useEffect(() => {
    if (!scrollHints || typeof window === 'undefined') return undefined;
    window.addEventListener('resize', updateFade);
    return () => window.removeEventListener('resize', updateFade);
  }, [scrollHints, updateFade]);

  /* Activation follows focus — switching a tab costs nothing here (the list is
     already loaded and the filter is a client-side pass), so the strip behaves
     like a rail: one arrow press, one visible change. Same call the
     `FiltersMasterDetail` rail makes, for the same reason. */
  /* ♿ **THE NEXT ENABLED TAB, WRAPPING — the whole of the disabled keyboard
     contract lives here.** Walking one step and checking is not enough: two
     adjacent disabled tabs (Timeline AND Labour go dead together on the
     Breakdown) would strand an ArrowRight on the first of them.
     🪤 **The loop is bounded by `tabs.length`, not by "until we find one".** An
     all-disabled strip is legal input and the naive form spins forever on it;
     this returns -1 and the key press simply does nothing.
     Called with `from = -1, step = 1` it yields the FIRST enabled tab and with
     `from = tabs.length, step = -1` the LAST — which is exactly `Home`/`End`,
     so those two need no second walker. */
  const nextEnabled = (from, step) => {
    const n = tabs.length;
    for (let k = 1; k <= n; k += 1) {
      const i = (((from + step * k) % n) + n) % n;
      if (!tabs[i].disabled) return i;
    }
    return -1;
  };

  const move = (nextIndex) => {
    // -1 means "every candidate was disabled" — decline rather than guess.
    const next = nextIndex < 0 ? null : tabs[nextIndex];
    if (!next) return;
    select(next.id);
    const el = tabRefs.current[next.id];
    if (el && typeof el.focus === 'function') el.focus();
  };

  const onKeyDown = (e) => {
    // An `active` matching no tab leaves `activeIndex` at -1; treat the strip as
    // sitting on its first tab so the keyboard still enters it somewhere real.
    const from = activeIndex < 0 ? 0 : activeIndex;
    switch (e.key) {
      case 'ArrowRight':
      case 'ArrowDown':
        e.preventDefault();
        move(nextEnabled(from, 1));
        break;
      case 'ArrowLeft':
      case 'ArrowUp':
        e.preventDefault();
        move(nextEnabled(from, -1));
        break;
      case 'Home':
        e.preventDefault();
        move(nextEnabled(-1, 1));
        break;
      case 'End':
        e.preventDefault();
        move(nextEnabled(tabs.length, -1));
        break;
      default:
        break;
    }
  };

  // A tablist with no tabs is not an empty tablist, it is nothing. Rendering
  // the shell would leave a bordered strip with a stray bottom rule on a page
  // whose tabs have not loaded.
  if (!tabs.length) return null;

  /* 🔑 The roving stop: the active tab, or — when `active` names no tab — the
     FIRST one. A strip where every `tabIndex` is -1 is a control no keyboard
     user can enter at all, which is a worse failure than a strip with nothing
     highlighted.
     🚫 **AND IT NEVER PARKS ON A DISABLED TAB.** Tab-ing into the strip and
     landing on a tab whose every key press is then declined reads as a broken
     control, not a declined one. When the fallback tab is dead we walk to the
     first enabled one instead; if they are ALL dead the stop goes back to 0, so
     the strip is still reachable and simply does nothing — the same choice the
     "no tab matches `active`" branch already makes. 🪤 The ACTIVE tab keeps the
     stop even if it is disabled: a caller that disables the tab you are looking
     at has a bug of its own, and silently moving focus elsewhere would hide it. */
  const fallbackIndex = tabs[0].disabled ? nextEnabled(-1, 1) : 0;
  const rovingIndex = activeIndex < 0
    ? (fallbackIndex < 0 ? 0 : fallbackIndex)
    : activeIndex;

  return (
    <div
      className={(bare
        ? className
        : `border-b border-gray-200 dark:border-gray-700 ${compact ? 'mb-3 max-md:mb-1' : 'mb-3'} ${className}`).trim()}
      data-testid={`${idPrefix}-strip`}
    >
      <div
        role="tablist"
        aria-label={ariaLabel}
        onKeyDown={onKeyDown}
        ref={listRef}
        {...(scrollHints || snapActive ? {
          onScroll: () => {
            if (scrollHints) updateFade();
            if (snapActive && userScroll.current.armed && !userScroll.current.touching) armSettle();
          },
        } : {})}
        {...snapHandlers}
        {...(scrollHints && (fade.left || fade.right) ? {
          style: {
            WebkitMaskImage: fadeMask(fade),
            maskImage: fadeMask(fade),
          },
          'data-fade': `${fade.left ? 'left' : ''}${fade.left && fade.right ? ' ' : ''}${fade.right ? 'right' : ''}`,
        } : {})}
        data-testid={`${idPrefix}-tablist`}
        // 🪤 A scroll box on ONE axis is a scroll box on BOTH (CSS: overflow-x non-visible
        // forces overflow-y to auto), and every tab hangs 1px below this row via
        // `-mb-px` to sit over the wrapper border — so a bare `overflow-x-auto` grew a
        // VERTICAL scrollbar on staging (owner, 2026-09-12). Above `sm` there is no
        // scroll box at all; below it the horizontal scroll stays and the vertical
        // axis is clipped (the active underline loses 1px there — accepted).
        className={(snapCenter
          ? 'flex items-end gap-1 overflow-x-auto overflow-y-hidden md:overflow-visible overscroll-x-contain snap-x snap-mandatory md:snap-none'
          : 'flex items-end gap-1 overflow-x-auto overflow-y-hidden sm:overflow-visible overscroll-x-contain')
          + (compact ? ` ${LIST_COMPACT}` : '')
          // 📱 No drawn scrollbar (owner, 2026-10-03: the moving blue underline
          // already shows the scroll — two bars read as two scrollbars). The
          // strip still scrolls; the edge fade still hints at more.
          + ' tab-strip-scrollbar'
          + (sliding ? ' relative' : '')}
        {...(snapCenter ? { 'data-snap': snapActive ? 'center' : 'off' } : {})}
        {...(snapActive && snapFlushStart ? { 'data-snap-start': 'flush' } : {})}
      >
        {tabs.map((t, i) => {
          const isActive = i === activeIndex;
          const isDisabled = Boolean(t.disabled);
          return (
            <button
              key={t.id}
              type="button"
              role="tab"
              id={`${idPrefix}-tab-${t.id}`}
              aria-selected={isActive}
              {...(panelId ? { 'aria-controls': panelId } : {})}
              /* 🚫 `aria-disabled` ONLY, never the native attribute — see the
                 docblock. Omitted entirely when enabled, so an unaffected
                 caller's DOM is byte-identical rather than carrying
                 `aria-disabled="false"` on every tab. */
              {...(isDisabled ? { 'aria-disabled': 'true' } : {})}
              /* The house pair's rule (`OrderItemReceivedControls`): the reason
                 is a title override that applies ONLY while disabled. */
              {...(isDisabled && t.disabledReason ? { title: t.disabledReason } : {})}
              tabIndex={i === rovingIndex ? 0 : -1}
              ref={(el) => { tabRefs.current[t.id] = el; }}
              onClick={() => {
                // A tap is not a scroll: the centring it causes must not settle.
                userScroll.current.armed = false;
                select(t.id);
              }}
              data-testid={`${idPrefix}-tab-${t.id}`}
              className={[
                equal ? TAB_BASE_EQUAL : TAB_BASE,
                bare ? '' : TAB_OVERLAP,
                snapCenter ? 'snap-center' : '',
                compact ? TAB_COMPACT : '',
                // eslint-disable-next-line no-nested-ternary
                isDisabled ? TAB_DISABLED : (isActive ? (sliding ? TAB_ON_SLIDING : TAB_ON) : TAB_OFF),
              ].filter(Boolean).join(' ')}
            >
              {equal ? t.label : <span>{t.label}</span>}
              {/* 🪤 `!= null`, never `t.count &&` — a queue with 0 in it is a
                  real, useful answer and the falsy form deletes it. */}
              {t.count != null && (
                <span
                  className={`${isActive ? COUNT_ON : COUNT_OFF}${compact ? ` ${COUNT_COMPACT}` : ''}`}
                  data-testid={`${idPrefix}-tab-count-${t.id}`}
                >
                  {t.count}
                </span>
              )}
            </button>
          );
        })}
        {sliding && <span ref={barRef} aria-hidden="true" className={SLIDING_BAR} data-testid={`${idPrefix}-slider`} />}
      </div>
    </div>
  );
}
